import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@material-ui/core";
import SectionTabs from "components/SectionTabs";
import SectionHeader from "components/SectionHeader";
import OrganizationUsers from "./List";
import RolesManagement from "./Roles";
import { getUser } from "utils/authentication";
import { TFunction } from "i18next";
import useSWR from "swr";
import * as api from "./utils";
import { useSnackbar } from "notistack";
import CreateBtn from "./List/CreateBtn";
import { CreateUserBody, UserRole } from "@dashboard-v3/api";

const usersTabs = ["USERS_LIST", "ROLES"] as const;
type UsersViewTab = (typeof usersTabs)[number];

export default function UserManagement() {
  const { t } = useTranslation(["users", "sidebar"]);
  const { enqueueSnackbar } = useSnackbar();
  const swr = useSWR("/users");
  const { search } = useLocation();
  const history = useHistory();
  const { role } = getUser();
  const currentTab = getCurrentTab(role, search);

  function handleTabChange(id: string) {
    const params = new URLSearchParams();
    params.set("tab", id);
    history.push(`/users?${params.toString()}`);
  }

  async function createUser(userData: CreateUserBody) {
    try {
      await api.createUser(userData);
      await swr.mutate();
    } catch (error) {
      console.error(error);
      api.handleError("postUser", error, errors => enqueueSnackbar(t(errors)));
    }
  }

  return (
    <>
      <SectionHeader
        title={t("sidebar:users")}
        {...(currentTab === "USERS_LIST" && {
          sideBtn: <CreateBtn onCreate={createUser} />,
        })}
      />
      {role === "OWNER" && (
        <SectionTabs
          tabs={getTabs(t)}
          onClick={handleTabChange}
          showTab={currentTab}
        />
      )}
      <Box mt="20px">
        {currentTab === "USERS_LIST" && <OrganizationUsers />}
        {currentTab === "ROLES" && <RolesManagement />}
      </Box>
    </>
  );
}

function getCurrentTab(role: UserRole, search: string): UsersViewTab {
  const tabParam = new URLSearchParams(search).get("tab");
  const isValid = usersTabs.includes(tabParam as UsersViewTab);

  if (!isValid) return "USERS_LIST";
  if (role !== "OWNER" && tabParam === "ROLES") return "USERS_LIST";

  return tabParam as UsersViewTab;
}

function getTabs(t: TFunction) {
  return usersTabs.map(tabId => ({
    id: tabId,
    label: t(`userTabs.${tabId}`),
  }));
}
